/**
 * CreateDeploymentTokenModal Component
 *
 * Modal for creating a new deployment token for CI/CD pipelines.
 * Displays the raw token only once upon creation.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { deploymentTokenCreateSchema } from '../../../lib/validators/deployment-token';
import type { DeploymentTokenCreateDTO, DeploymentTokenCreateResponse } from '../../../types/deployment-token.types';
import { useCreateDeploymentToken } from '../hooks/useCreateDeploymentToken';
import { getErrorMessage } from '../../../lib/utils/errors';

interface CreateDeploymentTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  applicationName: string;
}

/**
 * Generate token code from name
 * Converts "Production CI" to "production-ci"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

/**
 * CreateDeploymentTokenModal component
 */
export const CreateDeploymentTokenModal: React.FC<CreateDeploymentTokenModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  applicationName,
}) => {
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [createdToken, setCreatedToken] = useState<DeploymentTokenCreateResponse | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<DeploymentTokenCreateDTO>({
    resolver: zodResolver(deploymentTokenCreateSchema),
    defaultValues: {
      tokenCode: '',
      tokenName: '',
      expiresInDays: 30,
    },
  });

  const watchName = watch('tokenName');

  // Auto-generate token code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('tokenCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const createMutation = useCreateDeploymentToken({
    tenantId,
    applicationId,
    onSuccess: (data) => {
      console.log('Deployment token created:', data.tokenId);
      setCreatedToken(data);
    },
    onError: (error) => {
      console.error('Failed to create deployment token:', error);
    },
  });

  const handleClose = () => {
    reset();
    setAutoGenerateCode(true);
    setCreatedToken(null);
    setCopied(false);
    createMutation.reset();
    onClose();
  };

  const onSubmit = async (data: DeploymentTokenCreateDTO) => {
    createMutation.mutate(data);
  };

  const handleCopyToken = async () => {
    if (createdToken?.token) {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isLoading = createMutation.isPending;
  const error = createMutation.error;

  // If token was created, show the success view
  if (createdToken) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <Modal.Header>
          <span className="text-green-600">Token Created Successfully</span>
        </Modal.Header>

        <Modal.Body>
          <div className="space-y-4">
            <Alert variant="warning">
              <div className="space-y-2">
                <p className="font-semibold">Save this token now!</p>
                <p className="text-sm">
                  This is the only time you will see this token. Copy it and store it securely.
                  If you lose it, you will need to create a new token.
                </p>
              </div>
            </Alert>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Token Name:</span>
                <p className="text-gray-900 font-semibold">{createdToken.tokenName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Token Code:</span>
                <div className="mt-1">
                  <CopyableCode value={createdToken.tokenCode} />
                </div>
              </div>
              {createdToken.expiresAt && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Expires:</span>
                  <p className="text-gray-900">
                    {new Date(createdToken.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deployment Token
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdToken.token}
                  readOnly
                  className="flex-1 font-mono text-sm bg-gray-100 border border-gray-300 rounded-md px-3 py-2"
                />
                <Button
                  type="button"
                  variant={copied ? 'success' : 'secondary'}
                  onClick={handleCopyToken}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Use this token in your CI/CD pipeline with the Authorization header:
              </p>
              <code className="block mt-1 text-xs bg-gray-100 p-2 rounded font-mono overflow-x-auto">
                Authorization: Bearer {createdToken.token.slice(0, 20)}...
              </code>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Show the create form
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Create Deployment Token</Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Creating deployment token for <strong>{applicationName}</strong>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              This token can be used in CI/CD pipelines to deploy code bundles.
            </p>
          </div>

          {/* Token Name Field */}
          <div>
            <Input
              label="Token Name"
              placeholder="Production CI"
              {...register('tokenName')}
              error={errors.tokenName?.message}
              disabled={isLoading}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              A human-readable name to identify this token
            </p>
          </div>

          {/* Token Code Field */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Token Code <span className="text-red-500">*</span>
              </label>
              <label className="flex items-center text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  className="mr-1.5 h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isLoading}
                />
                Auto-generate from name
              </label>
            </div>
            <Input
              placeholder="production-ci"
              {...register('tokenCode')}
              error={errors.tokenCode?.message}
              disabled={isLoading || autoGenerateCode}
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier for this token within the application
            </p>
          </div>

          {/* Expiration Field */}
          <div>
            <Input
              label="Expiration (days)"
              type="number"
              placeholder="30"
              {...register('expiresInDays', { valueAsNumber: true })}
              error={errors.expiresInDays?.message}
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Number of days until the token expires (1-365, default 30)
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isLoading}
        >
          Create Token
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
